import { getFormattedCurrency } from "@src/shared/utils/getFormattedCurrency";
import * as React from "react";
import { useIntl } from "react-intl";

import * as sc from "./InputFilter.sc";

interface IInputFilterProps {
    value: string;
    onChange?: (value: string | null) => void;
    startAdornment?: string;
    endAdornment?: string;
    variant?: "default" | "dark";
    size?: "small" | "default";
    disabled?: boolean;
    maxLength?: number;
    maxValue?: number;
    minValue?: number;
    defaultValue?: number;
    startAdornmentWeight?: "default" | "bold";
    startAdornmentPadding?: number;
    endAdornmentWeight?: "default" | "bold";
    endAdornmentPadding?: number;
    isCurrency?: boolean;
    inputMode?: React.InputHTMLAttributes<unknown>["inputMode"];
    inputId?: string;
    labelId?: string;
}

const InputFilter: React.FunctionComponent<IInputFilterProps> = ({
    value,
    onChange,
    startAdornment,
    endAdornment,
    variant,
    size = "default",
    disabled,
    maxLength = 10,
    maxValue,
    minValue,
    defaultValue,
    startAdornmentWeight = "default",
    startAdornmentPadding = 0,
    endAdornmentWeight = "default",
    endAdornmentPadding = 0,
    isCurrency = false,
    inputMode,
    inputId,
    labelId,
}) => {
    const [internalValue, setInternalValue] = React.useState(value);
    const [isEditing, setIsEditing] = React.useState(false);

    React.useEffect(() => {
        setInternalValue(value);
    }, [value]);

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setInternalValue(event.currentTarget.value);
    };

    const handleBlur = () => {
        if (minValue !== undefined && maxValue !== undefined && defaultValue !== undefined) {
            let newValue = Number(internalValue);
            if (Number(internalValue) > maxValue) {
                newValue = defaultValue;
            }
            if (Number(internalValue) < minValue) {
                newValue = minValue;
            }

            setInternalValue(String(newValue));
            onChange?.(String(newValue));
        } else {
            onChange?.(internalValue);
        }
    };

    const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === "Enter") {
            handleBlur();
        }
    };

    const maxLengthCheck = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (event.target.value.length > event.target.maxLength) {
            event.target.value = event.target.value.slice(0, event.target.maxLength);
        }
    };

    const intl = useIntl();

    return (
        <sc.Root
            startAdornment={startAdornment}
            endAdornment={endAdornment}
            variant={variant}
            size={size}
            startAdornmentWeight={startAdornmentWeight}
            startAdornmentPadding={startAdornmentPadding}
            endAdornmentWeight={endAdornmentWeight}
            endAdornmentPadding={endAdornmentPadding}
        >
            <sc.Input
                type={isEditing ? "number" : "text"}
                value={
                    isEditing
                        ? internalValue
                        : isCurrency && !startAdornment && !endAdornment
                        ? getFormattedCurrency({ value: Number(internalValue) }, intl)
                        : intl.formatNumber(Number(internalValue))
                }
                onBlur={() => {
                    setIsEditing(false);
                    handleBlur();
                }}
                onChange={handleChange}
                onKeyDown={handleKeyDown}
                onFocus={() => setIsEditing(true)}
                hasStartAdornment={!!startAdornment}
                hasEndAdornment={!!endAdornment}
                endAdornment={endAdornment ? endAdornment : ""}
                variant={variant}
                disabled={disabled}
                maxLength={maxLength}
                onInput={maxLengthCheck}
                startAdornmentPadding={startAdornmentPadding}
                endAdornmentPadding={endAdornmentPadding}
                inputMode={inputMode}
                id={inputId}
                aria-labelledby={labelId}
            />
        </sc.Root>
    );
};
export default InputFilter;
